import React, { useEffect, useState } from 'react';
import {
  Text, TextInput, Trans, useTrans, WithSpinner,
} from 'ui';
import { useNotificationsManager } from 'hooks';
import useFormField, { makeForm } from 'hooks/useFormField';
import useDelayedState from 'hooks/useDelayedState';
import { isEmail } from 'utils/text';
import { useGetLocationQuery, useRestorePasswordMutationMutation } from 'queries';

import styled from 'ui/styled';
import Link from 'components/common/Link';
import useToggle from 'hooks/useToggle';
import locales, { LegalString } from './SignUp.strings';
import Icon from './view.svg';

import Form from '../../auth/Form/Form';
import { Flex, Layout } from '../../layout';
import { SignButton } from '../Button/Button';
import { SmmButtons } from '../Smm';
import { useLoginHelper } from '../Hooks/useLoginHelper';
import { NotificationType } from '../../../store/notifications';


const TogglePasswordIcon = styled(Icon)<{ hidden: boolean }>`
  opacity: ${({ hidden }) => (hidden ? 0.6 : 0.8)};
  transform: translateY(${({ hidden }) => (hidden ? '0px' : '3px')});
  cursor: pointer;
  pointer-events: auto;
  z-index: 1;
`;

const LegalText = styled.div`  
  & > span {
    line-height: 1.7;
  }
`;

const ForgotPassword = styled.button`
  border: none;
  border-bottom: 2px solid rgb(24, 102, 248);
  background: transparent;
  color: rgb(24, 102, 248);
  padding: 0 0 3px;
`;

export function SignUp({ isLogin }: { isLogin: boolean }) {
  const isRegister = !isLogin;
  const kind = isRegister ? 'signup' : 'login';

  const { t } = useTrans();
  const [isPasswordHidden, setPasswordHidden] = useState(true);
  const [hasAcceptedLegal, toggleAcceptedLegal] = useToggle(false);
  const isBrowser = useDelayedState(true);
  const location = useGetLocationQuery();
  const notificationManager = useNotificationsManager();

  const email = useFormField('', (value: string) => {
    if (!isEmail(value)) {
      throw new Error('Неверный email');
    }
  });
  const password = useFormField('', (pwd: string) => {
    if (!(pwd && pwd.length >= 5)) {
      throw new Error('минимум 5 символов');
    }
  });

  const togglePassword = () => setPasswordHidden(!isPasswordHidden);

  const form = makeForm({ email, password });
  const loginHelper = useLoginHelper(isRegister);

  const onSubmit = (evt) => {
    evt.preventDefault();
    loginHelper.submit(form.serialize());
  };

  const isLegalAlright = isRegister ? hasAcceptedLegal : true;
  const isButtonDisabled = !form.isValid || loginHelper.isLoading || !isBrowser || !isLegalAlright;

  useEffect(() => {
    if (loginHelper.error !== null) {
      const { error: { message, field } } = loginHelper;

      const input = field === 'email' ? email : password;
      input.setError(message);
    }
  }, [loginHelper.error]);

  // User may specify invalid email, not a password
  useEffect(() => {
    if (password.error && loginHelper.error) {
      password.setError(null);
    }
  }, [email.value]);

  useEffect(() => {
    if (isLogin) {
      loginHelper.getFromCredentials().catch(() => {});
    }
  }, [isLogin]);

  const [restartPasswordMutation] = useRestorePasswordMutationMutation();
  const initiateRestartSequence = async () => {
    loginHelper.setLoading(true);
    try {
      const resp = await restartPasswordMutation({
        variables: { email: email.value },
      });

      if (resp.data && resp.data.restorePassword.isSuccess) {
        notificationManager.addNotification(t(locales.restore.success));
      } else {
        notificationManager.addNotification(t(locales.restore.failed), NotificationType.Fail);
      }
    } catch (e) {
      notificationManager.addNotification(t(locales.restore.failed), NotificationType.Fail);
    }
    loginHelper.setLoading(false);
  };

  if (location.data && location.data.locationInfo && !location.data.locationInfo.isAvailableToRegister) {
    return (
      <Form>
        <Text weight={300} size="xl">
          <p>
YouThink is not yet available in your area (
            {location.data.locationInfo.country.message}
            ).
          </p>
          <p style={{ marginTop: 10 }}>Contact us if you want to know when it will be available in your country.</p>
        </Text>
      </Form>
    );
  }

  return (
    <Form onSubmit={onSubmit} id={isLogin ? 'login' : 'signup'}>
      <Layout justify="space-between" align="center">
        <Text weight={300} size="xxl">
          <Trans {...locales[kind].title} />
        </Text>
        <Link href={`/auth/sign${isRegister ? 'in' : 'up'}`} colored><Trans {...locales[kind].alternateOffer} /></Link>
      </Layout>
      <Flex basis={40} />
      <SmmButtons isLogin={isLogin} />
      <Flex basis={30} />
      <TextInput
        id="email"
        type="email"
        label={t(locales.signup.email)}
        autoComplete="username email"
        {...email}
      />
      <Flex basis={40} />
      <TextInput
        id="password"
        minLength={5}
        label={t(locales.signup.password)}
        type={isPasswordHidden ? 'password' : 'text'}
        autoComplete={isLogin ? 'current-password' : 'new-password'}
        right={(
          <TogglePasswordIcon
            hidden={isPasswordHidden}
            onClick={togglePassword}
          />
        )}
        {...password}
      />
      {isLogin ? (
        <>
          <Flex basis={30} />
          <div>
            <ForgotPassword
              type="button"
              onClick={initiateRestartSequence}
              disabled={loginHelper.isLoading}
            >
              <Trans {...locales.login.restorePassword} />
            </ForgotPassword>
          </div>
        </>
      ) : null}
      <Flex basis={40} />

      {isRegister ? (
        <Layout pb={20}>
          <LegalText>
            <Text color="middle">
              <LegalString checked={hasAcceptedLegal} onClick={() => toggleAcceptedLegal()} />
            </Text>
          </LegalText>
        </Layout>
      ) : null}

      <SignButton type="submit" disabled={isButtonDisabled}>
        <WithSpinner show={loginHelper.isLoading}><Trans {...locales[kind].cta} /></WithSpinner>
      </SignButton>
    </Form>
  );
}
